import React, { useEffect, useRef, useState } from 'react';
import { 
  Bed, Users, Maximize, Calendar, Phone, MapPin, Heart, Flame, Navigation
} from 'lucide-react';

const Rooms = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const fullAddress = "2, Sri Ram Nagar Colony, Near Kailashpuri Seva Aashram, Mahmoorganj, Ranipur Road, In Front Of Banaras Railway Station Road, Varanasi-221106, Uttar Pradesh";

const rooms = [
  {
    id: 1,
    name: 'Single Room',
    type: 'Non AC',
    price: 799,
    occupancy: 1,
    bedType: 'Single Bed',
    size: '180 sq ft',
    image: 'image1.png',
    description: 'Comfortable single non AC room in Varanasi.',
  },
  {
    id: 2,
    name: 'Double Room',
    type: 'Non AC',
    price: 1199,
    occupancy: 2,
    bedType: 'Double Bed',
    size: '220 sq ft',
    image: 'image2.png',
    description: 'Spacious double non AC room for family stay.',
  },
  {
    id: 3,
    name: 'Single Room',
    type: 'AC',
    price: 899,
    occupancy: 1,
    bedType: 'Single Bed',
    size: '200 sq ft',
    image: 'image3.png',
    description: 'AC single room with modern comfort.',
  },
  {
    id: 4,
    name: 'Double Room',
    type: 'AC',
    price: 1299,
    occupancy: 2,
    bedType: 'King Bed',
    size: '300 sq ft',
    image: 'image4.png',
    description: 'Premium AC double room with full amenities.',
  },
];

  return (
    <section id="rooms" ref={sectionRef} className="py-20 bg-gradient-to-br from-white via-orange-50/20 to-blue-50/20">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-orange-100 px-5 py-2 rounded-full mb-3">
            <Flame size={18} className="text-orange-600" />
            <span className="text-orange-700 font-semibold text-sm">Varanasi - Banaras Railway Station Road</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Our <span className="text-orange-600">Rooms</span> in <span className="text-blue-600">Varanasi</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-500 to-blue-500 mx-auto rounded-full mb-5"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Located at Sri Ram Nagar Colony, In Front of Banaras Railway Station Road
          </p>
        </div>

        {/* EXACT ADDRESS */}
        <div className="mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 shadow-xl">
          <div className="flex items-start gap-3 text-white">
            <MapPin size={24} className="flex-shrink-0 mt-1" />
            <div>
              <p className="text-sm font-semibold mb-1">📍 Our Address</p>
              <p className="text-sm opacity-90 leading-relaxed">
                {fullAddress}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="bg-white/20 px-2 py-1 rounded-lg text-xs">In Front of Banaras Railway Station Road</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg text-xs">Mahmoorganj</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg text-xs">Ranipur Road</span>
              </div>
            </div>
          </div>
        </div>

        {/* Rooms Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {rooms.map((room) => (
            <div
              key={room.id}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
                {room.discount && (
                  <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-lg text-xs font-bold">
                    {room.discount}% OFF
                  </div>
                )}
                <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-lg text-xs flex items-center gap-1">
                  <MapPin size={12} />
                  Ranipur Road
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Room Name */}
                <h3 className="text-lg font-bold text-gray-800">{room.name}</h3>
                
                {/* AC / Non AC Badge */}
                <div className="mt-1">
                  {room.type === 'AC' ? (
                    <span className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                      ❄️ AC
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                      🔥 Non AC
                    </span>
                  )}
                </div>

                {/* Bed & Occupancy */}
                <div className="flex items-center gap-3 text-xs text-gray-500 mt-3">
                  <span className="flex items-center gap-1">
                    <Bed size={12} />
                    {room.bedType}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={12} />
                    {room.occupancy} Guest
                  </span>
                  <span className="flex items-center gap-1">
                    <Maximize size={12} />
                    {room.size}
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-xs mt-2 line-clamp-2">{room.description}</p>

                {/* Price & Book Button */}
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xl font-bold text-orange-600">₹{room.price}</span>
                    <span className="text-xs text-gray-400 line-through">₹{room.originalPrice}</span>
                    <span className="text-xs font-semibold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                      {room.discount}% OFF
                    </span>
                  </div>
                  
                  <button
                    onClick={() => {
                      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full mt-3 bg-gradient-to-r from-orange-500 to-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:scale-105 transition-all duration-300 flex items-center justify-center gap-1"
                  >
                    <Calendar size={14} />
                    Book Now
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Location & Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-gray-100 rounded-2xl p-6 mb-6">
            <p className="text-gray-700 mb-3 flex items-center justify-center gap-2">
              <Navigation size={18} className="text-blue-600" />
              <strong>📍 Address:</strong> {fullAddress}
            </p>
            <p className="text-gray-500 text-sm">In Front of Banaras Railway Station Road | 5 min walk from Railway Station</p>
          </div>
          
          <a
            href="tel:+919555040589"
            className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-all duration-300 shadow-lg"
          >
            <Phone size={18} />
            Call for Booking: +91 95550 40589
          </a>
        </div>
      </div>
    </section>
  );
};

export default Rooms;